import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Plus, Save, Trash2, X, Star, RefreshCw, ExternalLink, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

const EMPTY = {
  name: "", slug: "", description: "",
  price_monthly_cents: 0, price_yearly_cents: 0,
  stripe_price_monthly: "", stripe_price_yearly: "",
  max_cvs: -1, max_letters_per_month: -1, max_applications: -1,
  max_downloads_per_month: -1,
  max_templates: -1, max_match_ai_per_month: -1, max_languages: 5,
  allow_public_cv: true, allow_ai_interview: true,
  allow_ai_match: true, allow_cv_variants: true, allow_letters: true, allow_bookmarks: true,
  is_default: false, active: true, sort_order: 0, commission_pct: 15.0,
  is_featured: false,
};

const cents = (v) => `€ ${(Number(v || 0) / 100).toFixed(2)}`;
const parseEuros = (str) => Math.round((Number(String(str).replace(",", ".")) || 0) * 100);
const stripeUrl = (priceId) => `https://dashboard.stripe.com/prices/${priceId}`;

/**
 * LimitField — one of the numeric-quota inputs on a plan.
 *
 * Convention (matches backend since 2026-02-15):
 *   -1 → "Onbeperkt" checkbox is on, numeric input is disabled
 *    0 → hard block (feature literally cannot be used on this plan)
 *   >0 → exact limit
 */
function LimitField({ label, value, onChange, testId }) {
  const isUnlimited = Number(value) < 0;
  return (
    <label className="block">
      <span className="ed-label mb-1 block">{label}</span>
      <div className="flex items-stretch gap-2">
        <input
          type="number"
          min={0}
          data-testid={testId}
          className="ed-input flex-1 disabled:bg-neutral-100 disabled:text-neutral-400"
          value={isUnlimited ? "" : value}
          placeholder={isUnlimited ? "onbeperkt" : "0 = blokkeer"}
          disabled={isUnlimited}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw === "" ? 0 : Math.max(0, Number(raw) || 0));
          }}
        />
        <label
          className={`inline-flex items-center gap-1.5 px-3 border cursor-pointer font-mono text-[0.65rem] uppercase tracking-[0.12em] whitespace-nowrap select-none ${
            isUnlimited ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]" : "border-neutral-300 hover:border-neutral-500"
          }`}
          title="Geen limiet voor deze feature"
        >
          <input
            type="checkbox"
            data-testid={testId ? `${testId}-unlimited` : undefined}
            className="sr-only"
            checked={isUnlimited}
            onChange={(e) => onChange(e.target.checked ? -1 : 0)}
          />
          <span className={`w-3 h-3 border flex items-center justify-center ${isUnlimited ? "bg-[#F9F8F6] border-[#F9F8F6]" : "border-neutral-400"}`}>
            {isUnlimited && (
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="3">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </span>
          Onbeperkt
        </label>
      </div>
      <span className="text-[0.6rem] font-mono text-neutral-500">
        {isUnlimited ? "Geen limiet" : Number(value) === 0 ? "0 = feature volledig geblokkeerd op dit abonnement" : `Max ${value} per periode`}
      </span>
    </label>
  );
}

function ToggleField({ label, checked, onChange, testId }) {
  return (
    <button
      type="button" data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`w-full flex items-center gap-2 border px-3 py-2 text-left font-mono text-[0.65rem] uppercase tracking-[0.12em] transition-colors ${checked ? "border-neutral-900 bg-white" : "border-neutral-300 hover:border-neutral-500"}`}
    >
      <span className={`w-3.5 h-3.5 border flex items-center justify-center ${checked ? "bg-neutral-900 border-neutral-900" : "border-neutral-400"}`}>
        {checked && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>}
      </span>
      {label}
    </button>
  );
}

// Preset access-pack durations. Admins pick one — the raw `days_monthly`
// value is stored on the plan doc so the checkout endpoint knows exactly
// how many days to grant on purchase.
const DURATION_PRESETS = [
  { key: "7d", days: 7, labelNl: "1 week", labelEn: "1 week" },
  { key: "14d", days: 14, labelNl: "2 weken", labelEn: "2 weeks" },
  { key: "30d", days: 30, labelNl: "1 maand", labelEn: "1 month" },
  { key: "90d", days: 90, labelNl: "3 maanden", labelEn: "3 months" },
  { key: "180d", days: 180, labelNl: "6 maanden", labelEn: "6 months" },
  { key: "365d", days: 365, labelNl: "1 jaar", labelEn: "1 year" },
];

// Convert a euro-string ("24" or "24,50") to Stripe minor units (cents).
const eurosStringToCents = (s) => {
  const n = Number(String(s).replace(",", ".")) || 0;
  return Math.round(n * 100);
};

function PlanEditor({ plan, onSave, onCancel, saving, allPlans = [] }) {
  const { t, lang } = useT();
  const [f, setF] = useState(plan || EMPTY);
  const upd = (patch) => setF((cur) => ({ ...cur, ...patch }));

  // Local string state so admins can *clear* the fields (blank input) —
  // otherwise React re-renders "0.00" back into the box on every keystroke.
  const [yearly, setYearly] = useState(
    () => (Number(plan?.price_yearly_cents ?? 0) > 0
      ? (Number(plan.price_yearly_cents) / 100).toFixed(2)
      : ""),
  );

  // Since 2026-02-16 the "monthly" price field is the ONE-TIME total that
  // Stripe charges — no more per-month multiplication. Keeping the DB field
  // name `price_monthly_cents` for backward compatibility with existing
  // records and downstream reports.
  const [totalPrice, setTotalPrice] = useState(
    () => {
      const cents = Number(plan?.price_monthly_cents ?? 0);
      if (cents <= 0) return "";
      return (cents / 100).toFixed(2);
    },
  );
  const totalCharge = eurosStringToCents(totalPrice);
  const perMonthEquiv = (totalCharge > 0 && f.days_monthly > 0)
    ? (totalCharge / 100 / (f.days_monthly / 30))
    : 0;

  // Keep `f.price_monthly_cents` in sync with the raw input.
  useEffect(() => {
    upd({ price_monthly_cents: totalCharge });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPrice]);

  // Yearly cents follows the local string field.
  useEffect(() => {
    upd({ price_yearly_cents: yearly === "" ? 0 : eurosStringToCents(yearly) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearly]);

  // Validation: a longer pack should never cost MORE per month than a
  // shorter one. Warn the admin when their pricing breaks that promise.
  const validationWarning = (() => {
    if (!f.days_monthly || f.price_monthly_cents <= 0) return null;
    const others = (allPlans || []).filter(
      (p) => p.slug !== f.slug && (p.days_monthly || 0) > 0 && (p.price_monthly_cents || 0) > 0
    );
    // Compute the effective per-month rate from the one-time total for
    // comparison against other packs.
    const myRate = (f.days_monthly > 0)
      ? (Number(f.price_monthly_cents) / 100) / (f.days_monthly / 30)
      : 0;
    for (const other of others) {
      const otherRate = (other.price_monthly_cents / 100) / (other.days_monthly / 30);
      if (f.days_monthly > other.days_monthly && myRate > otherRate) {
        return t("plnValidationLongerCheaper", { name: other.name });
      }
      if (f.days_monthly < other.days_monthly && myRate < otherRate) {
        return t("plnValidationShorterHigher", { name: other.name });
      }
    }
    return null;
  })();

  return (
    <div className="ed-card p-6 space-y-5" data-testid="plan-editor">
      <div className="flex items-center justify-between">
        <p className="ed-eyebrow">{plan?.id ? t("plnEdit") : t("plnNew")}</p>
        <button data-testid="plan-editor-close" onClick={onCancel} className="p-1.5 border border-neutral-300 hover:border-neutral-900">
          <X size={12} strokeWidth={1.5} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="ed-label mb-1 block">{t("plnName")}</span>
          <input data-testid="plan-name" className="ed-input" value={f.name}
                 onChange={(e) => upd({ name: e.target.value })} />
        </label>
        <label className="block">
          <span className="ed-label mb-1 block">{t("plnSlug")}</span>
          <input data-testid="plan-slug" className="ed-input font-mono" placeholder="basic"
                 value={f.slug} onChange={(e) => upd({ slug: e.target.value.toLowerCase() })}
                 disabled={f.slug === "trial"} />
        </label>
      </div>

      <label className="block">
        <span className="ed-label mb-1 block">{t("plnDescription")}</span>
        <textarea data-testid="plan-desc" className="ed-input min-h-[3.5rem]"
                  value={f.description} onChange={(e) => upd({ description: e.target.value })} />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="ed-label mb-1 block">{t("plnPriceMonthly")}</span>
          <input type="text" inputMode="decimal" data-testid="plan-price-monthly" className="ed-input"
                 placeholder="0.00"
                 value={totalPrice} onChange={(e) => setTotalPrice(e.target.value)} />
          <span className="font-mono text-[0.6rem] text-neutral-500 mt-1 block">
            {totalCharge > 0
              ? t("plnPriceMonthlyHint", { total: (totalCharge / 100).toFixed(2), days: f.days_monthly || 30, permonth: perMonthEquiv.toFixed(2) })
              : t("plnPriceMonthlyHintEmpty")}
          </span>
        </label>
        <label className="block">
          <span className="ed-label mb-1 block">{t("plnPriceYearly")}</span>
          <input type="text" inputMode="decimal" data-testid="plan-price-yearly" className="ed-input"
                 placeholder="leeg = niet gebruikt"
                 value={yearly} onChange={(e) => setYearly(e.target.value)} />
          <span className="font-mono text-[0.6rem] text-neutral-500 mt-1 block">
            {t("plnPriceYearlyHint")}
          </span>
        </label>
        <label className="block sm:col-span-2">
          <span className="ed-label mb-1 flex items-center gap-2">
            {t("plnCommission")}
            <span className="font-mono text-[0.6rem] text-neutral-500 normal-case tracking-normal">
              {t("plnCommissionHint", { euro: ((Number(f.price_monthly_cents || 0) / 100) * (Number(f.commission_pct || 0) / 100)).toFixed(2) })}
            </span>
          </span>
          <div className="flex items-stretch gap-2">
            <input
              type="number"
              min={0}
              max={50}
              step={0.5}
              data-testid="plan-commission-pct"
              className="ed-input w-24"
              value={f.commission_pct ?? 15}
              onChange={(e) => upd({ commission_pct: Math.max(0, Math.min(50, Number(e.target.value) || 0)) })}
            />
            <span className="font-mono text-sm text-neutral-500 self-center">%</span>
          </div>
        </label>
        <label className="block">
          <span className="ed-label mb-1 block">{t("plnStripeMonthly")}</span>
          <div className="flex items-stretch gap-1.5">
            <input data-testid="plan-stripe-monthly" className="ed-input font-mono text-[0.7rem] flex-1" placeholder="price_..."
                   value={f.stripe_price_monthly}
                   onChange={(e) => upd({ stripe_price_monthly: e.target.value })} />
            {f.stripe_price_monthly && (
              <a
                data-testid="plan-stripe-monthly-link"
                href={stripeUrl(f.stripe_price_monthly)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-2 border border-neutral-300 hover:border-neutral-900"
                title="Open in Stripe Dashboard"
              >
                <ExternalLink size={12} strokeWidth={1.5} />
              </a>
            )}
          </div>
        </label>
        <label className="block">
          <span className="ed-label mb-1 block">{t("plnStripeYearly")}</span>
          <div className="flex items-stretch gap-1.5">
            <input data-testid="plan-stripe-yearly" className="ed-input font-mono text-[0.7rem] flex-1" placeholder="price_..."
                   value={f.stripe_price_yearly}
                   onChange={(e) => upd({ stripe_price_yearly: e.target.value })} />
            {f.stripe_price_yearly && (
              <a
                data-testid="plan-stripe-yearly-link"
                href={stripeUrl(f.stripe_price_yearly)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-2 border border-neutral-300 hover:border-neutral-900"
                title="Open in Stripe Dashboard"
              >
                <ExternalLink size={12} strokeWidth={1.5} />
              </a>
            )}
          </div>
        </label>
      </div>

      <div>
        <p className="ed-label mb-2">{t("plnLimits")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <LimitField label={t("plnMaxCvs")} value={f.max_cvs} onChange={(v) => upd({ max_cvs: v })} testId="plan-max-cvs" />
          <LimitField label={t("plnMaxLetters")} value={f.max_letters_per_month} onChange={(v) => upd({ max_letters_per_month: v })} testId="plan-max-letters" />
          <LimitField label={t("plnMaxApps")} value={f.max_applications} onChange={(v) => upd({ max_applications: v })} testId="plan-max-apps" />
          <LimitField label={t("plnMaxDownloads")} value={f.max_downloads_per_month} onChange={(v) => upd({ max_downloads_per_month: v })} testId="plan-max-downloads" />
          <LimitField label={t("plnMaxTemplates")} value={f.max_templates} onChange={(v) => upd({ max_templates: v })} testId="plan-max-templates" />
          <LimitField label={t("plnMaxMatchAi")} value={f.max_match_ai_per_month} onChange={(v) => upd({ max_match_ai_per_month: v })} testId="plan-max-match-ai" />
          <LimitField label={t("plnMaxLanguages")} value={f.max_languages} hint="1–5 (NL, EN, DE, FR, ES)" onChange={(v) => upd({ max_languages: Math.max(1, Math.min(5, v)) })} testId="plan-max-languages" />
        </div>
      </div>

      <div>
        <p className="ed-label mb-2">{t("plnFeatures")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <ToggleField label={t("plnPublicCv")} checked={f.allow_public_cv} onChange={(v) => upd({ allow_public_cv: v })} testId="plan-toggle-public" />
          <ToggleField label={t("plnAiInterview")} checked={f.allow_ai_interview} onChange={(v) => upd({ allow_ai_interview: v })} testId="plan-toggle-ai" />
          <ToggleField label={t("plnIsDefault")} checked={f.is_default} onChange={(v) => upd({ is_default: v })} testId="plan-toggle-default" />
          <ToggleField label={t("plnIsFeatured")} checked={!!f.is_featured} onChange={(v) => upd({ is_featured: v })} testId="plan-toggle-featured" />
          <ToggleField label={t("plnActive")} checked={f.active} onChange={(v) => upd({ active: v })} testId="plan-toggle-active" />
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          data-testid="plan-save"
          onClick={() => onSave(f)}
          disabled={saving || !f.name.trim()}
          className="inline-flex items-center gap-2 bg-neutral-900 text-[#F9F8F6] px-5 py-2.5 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:bg-neutral-700 transition-colors disabled:opacity-50"
        >
          <Save size={12} strokeWidth={1.5} /> {t("plnSave")}
        </button>
        <button
          data-testid="plan-cancel"
          onClick={onCancel}
          className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 underline underline-offset-2"
        >
          {t("plnCancel")}
        </button>
      </div>
    </div>
  );
}

export default function PlansAdmin() {
  const confirm = useConfirm();
  const { t } = useT();
  const [plans, setPlans] = useState(null);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} | {plan}
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const handleDrop = useCallback(async (targetIdx) => {
    if (dragIdx === null || !plans || dragIdx === targetIdx) { setDragIdx(null); setDragOverIdx(null); return; }
    const next = [...plans];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setPlans(next);
    setDragIdx(null); setDragOverIdx(null);
    try {
      await api.post("/admin/plans/reorder", { order: next.map((p) => p.id) });
      toast.success(t("plnOrderSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("plnOrderFailed"));
    }
  }, [dragIdx, plans, t]);

  const load = useCallback(async () => {
    try {
      const [{ data: p }, statsRes] = await Promise.all([
        api.get("/admin/plans"),
        api.get("/admin/plans/stats").catch(() => ({ data: null })),
      ]);
      setPlans(p);
      setStats(statsRes?.data || null);
    } catch {
      toast.error(t("plnLoadFailed"));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      let synced;
      if (payload.id) {
        const { data } = await api.patch(`/admin/plans/${payload.id}`, payload);
        synced = data?._stripe_sync;
        toast.success(t("plnUpdated"));
      } else {
        const { data } = await api.post("/admin/plans", payload);
        synced = data?._stripe_sync;
        toast.success(t("plnCreated"));
      }
      // Surface Stripe auto-sync result as a secondary toast when something was created
      if (synced) {
        if (synced.error) {
          toast.error(`${t("plnStripeSyncFailed")} — ${synced.error}`);
        } else {
          const parts = [];
          if (synced.product) parts.push(`Product ${synced.product}`);
          if (synced.monthly) parts.push(`${t("plnPriceMonthly")}: ${synced.monthly}`);
          if (synced.yearly) parts.push(`${t("plnPriceYearly")}: ${synced.yearly}`);
          if (parts.length) {
            toast.success(`Stripe ✓ · ${parts.join(" · ")}`, { duration: 6000 });
          }
        }
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("plnSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!(await confirm(t("plnConfirmDelete").replace("{name}", plan.name)))) return;
    try {
      await api.delete(`/admin/plans/${plan.id}`);
      toast.success(t("plnDeleted"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("plnDeleteFailed"));
    }
  };

  return (
    <section className="mt-14" data-testid="plans-admin">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <p className="ed-eyebrow">{t("plnSectionEyebrow")}</p>
          <h2 className="font-serif text-2xl leading-tight mt-1">{t("plnSectionTitle")}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="plans-refresh"
            onClick={load}
            className="p-2 border border-neutral-300 hover:border-neutral-900"
            title={t("plnRefresh")}
          >
            <RefreshCw size={13} strokeWidth={1.5} />
          </button>
          <button
            data-testid="plans-new"
            onClick={() => setEditing(EMPTY)}
            className="inline-flex items-center gap-2 bg-neutral-900 text-[#F9F8F6] px-4 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:bg-neutral-700 transition-colors"
          >
            <Plus size={12} strokeWidth={1.5} /> {t("plnNewCta")}
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="plans-stats-totals">
          <div className="ed-card p-4">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">{t("plnStatActive")}</p>
            <p className="font-serif text-3xl mt-2" data-testid="plans-stat-active-total">{stats.total.active}</p>
          </div>
          <div className="ed-card p-4">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">{t("plnStatMrr")}</p>
            <p className="font-serif text-3xl mt-2" data-testid="plans-stat-mrr">{cents(stats.total.mrr_cents)}</p>
          </div>
          <div className="ed-card p-4">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">{t("plnStatArr")}</p>
            <p className="font-serif text-3xl mt-2" data-testid="plans-stat-arr">{cents(stats.total.arr_cents)}</p>
          </div>
        </div>
      )}

      {editing && (
        <div className="mb-6">
          <PlanEditor
            plan={editing}
            saving={saving}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      {plans === null ? (
        <p className="ed-eyebrow py-6">{t("loading")}</p>
      ) : plans.length === 0 ? (
        <p className="text-sm text-neutral-500 italic font-serif py-4">{t("plnEmpty")}</p>
      ) : (
        <div className="ed-card overflow-x-auto">
          <table className="ed-table w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="px-2 py-3 w-6"></th>
                <th className="px-4 py-3 text-left font-mono text-[0.6rem] uppercase tracking-[0.12em]">{t("plnColName")}</th>
                <th className="px-4 py-3 text-left font-mono text-[0.6rem] uppercase tracking-[0.12em]">{t("plnColPrice")}</th>
                <th className="px-4 py-3 text-left font-mono text-[0.6rem] uppercase tracking-[0.12em]">{t("plnColCommission")}</th>
                <th className="px-4 py-3 text-left font-mono text-[0.6rem] uppercase tracking-[0.12em]">{t("plnColLimits")}</th>
                <th className="px-4 py-3 text-left font-mono text-[0.6rem] uppercase tracking-[0.12em]">{t("plnColStatus")}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {plans.map((p, idx) => {
                const s = stats?.per_plan?.find((x) => x.slug === p.slug);
                return (
                <tr
                  key={p.id}
                  data-testid={`plan-row-${p.slug}`}
                  className={`border-b border-neutral-100 last:border-b-0 ${dragOverIdx === idx ? "bg-neutral-50" : ""}`}
                  draggable
                  onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; }}
                  onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); e.dataTransfer.dropEffect = "move"; }}
                  onDragLeave={() => setDragOverIdx((cur) => (cur === idx ? null : cur))}
                  onDrop={(e) => { e.preventDefault(); handleDrop(idx); }}
                  onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                >
                  <td className="px-2 py-3 align-top text-neutral-400 cursor-grab active:cursor-grabbing" data-testid={`plan-drag-${p.slug}`}>
                    <GripVertical size={13} strokeWidth={1.5} />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="font-serif text-base flex items-center gap-2">
                      {p.name}
                      {p.is_default && <Star size={12} strokeWidth={2} className="text-amber-600" fill="currentColor" />}
                    </div>
                    <div className="font-mono text-[0.6rem] text-neutral-500 uppercase tracking-[0.12em]">{p.slug}</div>
                    {s && s.active > 0 && (
                      <div className="mt-2 font-mono text-[0.65rem] text-emerald-700" data-testid={`plan-stats-${p.slug}`}>
                        {s.active} {t("plnStatActiveShort")} · {cents(s.mrr_cents)} MRR
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top font-mono text-[0.75rem]">
                    <div className="flex items-center gap-1.5">
                      <span>{cents(p.price_monthly_cents)}{" "}
                        <span className="text-neutral-400">
                          {(p.days_monthly || 0) > 0 ? "eenmalig" : "/ mnd"}
                        </span>
                        {(p.days_monthly || 0) > 0 && (
                          <span className="text-neutral-400 ml-1">· {p.days_monthly}d</span>
                        )}
                        {s && s.monthly > 0 ? <span className="text-emerald-700 ml-1">· {s.monthly}</span> : null}
                      </span>
                      {p.stripe_price_monthly && (
                        <a data-testid={`plan-stripe-link-monthly-${p.slug}`} href={stripeUrl(p.stripe_price_monthly)} target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-neutral-900" title="Stripe Dashboard">
                          <ExternalLink size={10} strokeWidth={1.5} />
                        </a>
                      )}
                    </div>
                    {(p.price_yearly_cents || 0) > 0 && (
                      <div className="flex items-center gap-1.5">
                        <span>{cents(p.price_yearly_cents)} <span className="text-neutral-400">/ jr</span>{s && s.yearly > 0 ? <span className="text-emerald-700 ml-1">· {s.yearly}</span> : null}</span>
                        {p.stripe_price_yearly && (
                          <a data-testid={`plan-stripe-link-yearly-${p.slug}`} href={stripeUrl(p.stripe_price_yearly)} target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-neutral-900" title="Stripe Dashboard">
                            <ExternalLink size={10} strokeWidth={1.5} />
                          </a>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top" data-testid={`plan-commission-${p.slug}`}>
                    <div className="font-serif text-lg tabular-nums leading-none">
                      {(p.commission_pct ?? 15)}<span className="text-neutral-400 text-sm">%</span>
                    </div>
                    <div className="font-mono text-[0.6rem] text-neutral-500 mt-1">
                      € {(((Number(p.price_monthly_cents) || 0) / 100) * ((Number(p.commission_pct) || 0) / 100)).toFixed(2)}
                      <span className="text-neutral-400"> / verkoop</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top font-mono text-[0.7rem] leading-tight text-neutral-600">
                    CV {p.max_cvs || "∞"} · Brief {p.max_letters_per_month || "∞"}<br />
                    Solli {p.max_applications || "∞"} · Downloads {p.max_downloads_per_month || "∞"}<br />
                    Tpl {p.max_templates || "∞"} · Talen {p.max_languages}<br />
                    Match-AI {p.max_match_ai_per_month || "∞"}<br />
                    {p.allow_public_cv ? "🔗 " : ""}{p.allow_ai_interview ? "🎯" : ""}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-col gap-1.5 items-start">
                      <span className={`font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2 py-1 border ${p.active ? "border-emerald-700 text-emerald-700" : "border-neutral-400 text-neutral-500"}`}>
                        {p.active ? t("plnStatusActive") : t("plnStatusInactive")}
                      </span>
                      {/* Public-visibility indicator: a plan only appears on
                          /pricing when `active=true` AND `days_monthly > 0`.
                          Show a small badge so admins immediately see which
                          plans are hidden from customers. */}
                      {/* Public-visibility indicator: a plan appears on
                          /pricing when `active=true` AND (`days_monthly > 0`
                          OR `is_default` — the trial plan is always visible
                          when active, regardless of days). */}
                      {p.active && ((p.days_monthly || 0) > 0 || p.is_default) ? (
                        <span
                          className="font-mono text-[0.55rem] uppercase tracking-[0.12em] px-2 py-0.5 border border-neutral-900 bg-neutral-900 text-[#F9F8F6]"
                          data-testid={`plan-public-${p.slug}`}
                          title="Zichtbaar op de publieke /pricing pagina"
                        >
                          ● Publiek zichtbaar
                        </span>
                      ) : (
                        <span
                          className="font-mono text-[0.55rem] uppercase tracking-[0.12em] px-2 py-0.5 border border-amber-500 text-amber-700 bg-amber-50"
                          data-testid={`plan-hidden-${p.slug}`}
                          title={!p.active ? "Plan is niet actief" : "days_monthly = 0 — niet gefactureerd op /pricing"}
                        >
                          ○ Verborgen op /pricing
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        data-testid={`plan-edit-${p.slug}`}
                        onClick={() => setEditing(p)}
                        className="font-mono text-[0.6rem] uppercase tracking-[0.12em] border border-neutral-300 px-3 py-1.5 hover:border-neutral-900"
                      >
                        {t("plnEditBtn")}
                      </button>
                      {p.slug !== "trial" && (
                        <button
                          data-testid={`plan-delete-${p.slug}`}
                          onClick={() => handleDelete(p)}
                          className="p-1.5 border border-neutral-300 text-rose-700 hover:border-rose-700"
                          title={t("plnDeleteBtn")}
                        >
                          <Trash2 size={12} strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
